import React, { useEffect, useMemo } from 'react';
import { useGamingStore } from '../../store/gamingStore';
import { useAuthStore } from '../../store/authStore';
import {
  Play, Gamepad2, Star, TrendingUp, ChevronRight,
  Library, Compass, Sparkles, Zap, Swords, Globe, Clock,
  Trophy, Target, Joystick, Shield, Server,
} from 'lucide-react';
import GameCard from './GameCard';

const GamingHubHome: React.FC = () => {
  const {
    storeGames, libraryGames, featuredGames, profiles,
    isLoadingStore,
    fetchStoreGames, fetchLibrary, fetchProfiles,
    setGamingView, openGameDetail, launchProfile,
  } = useGamingStore();
  const { user } = useAuthStore();
  const isAdmin = !!user?.is_admin || localStorage.getItem('is_admin') === '1';

  useEffect(() => {
    fetchStoreGames();
    fetchLibrary();
    fetchProfiles();
  }, []);

  /* ── Merge recent profiles & games for Quick Launch ── */
  const recentItems = useMemo(() => {
    type Item = {
      type: 'profile' | 'game';
      id: string;
      name: string;
      lastPlayed: string;
      icon?: string | null;
      meta?: string;
    };
    const out: Item[] = [];
    (profiles ?? []).forEach(p => {
      if (p.last_played_at)
        out.push({
          type: 'profile', id: p.id, name: p.name,
          lastPlayed: p.last_played_at, icon: p.icon,
          meta: `${p.minecraft.version} · ${p.minecraft.loader}`,
        });
    });
    (libraryGames ?? []).forEach(g => {
      if (g.last_played_at)
        out.push({
          type: 'game', id: g.slug, name: g.title,
          lastPlayed: g.last_played_at!, icon: g.cover_image_url,
          meta: g.genre ?? undefined,
        });
    });
    return out
      .sort((a, b) => +new Date(b.lastPlayed) - +new Date(a.lastPlayed))
      .slice(0, 5);
  }, [profiles, libraryGames]);

  const heroGame = featuredGames?.[0] ?? storeGames?.[0];
  const popularGames = storeGames.filter(
    g => !featuredGames.some(f => f.id === g.id),
  );

  const timeAgo = (d: string) => {
    const m = Math.floor((Date.now() - +new Date(d)) / 60000);
    if (m < 60) return `il y a ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `il y a ${h}h`;
    return `il y a ${Math.floor(h / 24)}j`;
  };

  return (
    <div className="h-full overflow-y-auto gaming-scrollbar">
      <div className="space-y-6 pb-8">

        {/* ═══════════════════ HERO SPOTLIGHT ═══════════════════ */}
        {heroGame ? (
          <section
            className="relative h-64 lg:h-72 overflow-hidden cursor-pointer group"
            onClick={() => openGameDetail(heroGame.slug)}
          >
            {/* BG */}
            <div className="absolute inset-0">
              {heroGame.banner_image_url ? (
                <img
                  src={heroGame.banner_image_url}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  alt=""
                />
              ) : heroGame.cover_image_url ? (
                <img
                  src={heroGame.cover_image_url}
                  className="w-full h-full object-cover blur-2xl scale-125 transition-transform duration-700 group-hover:scale-[1.3]"
                  alt=""
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gaming-accent/20 via-purple-900/30 to-bg-primary" />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-bg-primary via-bg-primary/85 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-transparent to-bg-primary/40" />
            </div>

            {/* Bottom accent */}
            <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gaming-accent/40 to-transparent" />

            {/* Content */}
            <div className="relative z-10 h-full flex items-end px-8 pb-8">
              <div className="flex-1 max-w-lg">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-gaming-accent mb-2">
                  <Sparkles className="w-3 h-3" /> En vedette
                </span>
                <h1 className="text-3xl lg:text-4xl font-black text-white mb-1.5 leading-tight tracking-tight">
                  {heroGame.title}
                </h1>
                {heroGame.short_description && (
                  <p className="text-sm text-text-secondary line-clamp-2 mb-4 leading-relaxed">
                    {heroGame.short_description}
                  </p>
                )}
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gaming-accent text-black font-bold text-sm hover:bg-gaming-accent-hover transition-all">
                    <Play className="w-4 h-4 fill-current" /> Découvrir
                  </span>
                  {Number(heroGame.price) === 0 ? (
                    <span className="text-sm font-semibold text-emerald-400">Gratuit</span>
                  ) : heroGame.sale_price != null ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-text-muted line-through">{Number(heroGame.price).toFixed(2)}$</span>
                      <span className="text-sm font-bold text-gaming-accent">{Number(heroGame.sale_price).toFixed(2)}$</span>
                    </div>
                  ) : (
                    <span className="text-sm font-semibold text-text-primary">{Number(heroGame.price).toFixed(2)}$</span>
                  )}
                </div>
              </div>

              {heroGame.cover_image_url && (
                <div className="hidden lg:block w-32 aspect-[2/3] rounded-lg overflow-hidden shadow-2xl shadow-black/60 border border-white/10 ml-8 group-hover:scale-[1.03] transition-transform duration-500">
                  <img src={heroGame.cover_image_url} className="w-full h-full object-cover" alt="" />
                </div>
              )}
            </div>
          </section>
        ) : (
          <div className="h-64 lg:h-72 bg-bg-secondary animate-pulse" />
        )}

        {/* Padded wrapper */}
        <div className="max-w-7xl mx-auto px-6 space-y-7">

          {/* ═══════════════════ QUICK LAUNCH ═══════════════════ */}
          {recentItems.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-gaming-accent" />
                <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">
                  Continuer à jouer
                </h2>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-1 gaming-scrollbar">
                {recentItems.map(item => (
                  <button
                    key={`${item.type}-${item.id}`}
                    onClick={e => {
                      e.stopPropagation();
                      if (item.type === 'profile') {
                        setGamingView('minecraft');
                        launchProfile(item.id);
                      } else {
                        openGameDetail(item.id);
                      }
                    }}
                    className="flex-shrink-0 group/qi flex items-center gap-3 bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-2.5 pr-4 hover:border-gaming-accent/40 hover:bg-bg-tertiary transition-all duration-200"
                  >
                    <div className="w-11 h-11 rounded-lg overflow-hidden bg-bg-tertiary flex-shrink-0">
                      {item.icon ? (
                        <img src={item.icon} className="w-full h-full object-cover" alt="" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gaming-accent/20 to-bg-tertiary">
                          <Gamepad2 className="w-4 h-4 text-gaming-accent/50" />
                        </div>
                      )}
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-xs font-semibold text-text-primary truncate max-w-[120px]">
                        {item.name}
                      </p>
                      <p className="text-[10px] text-text-muted flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" /> {timeAgo(item.lastPlayed)}
                      </p>
                    </div>
                    <div className="ml-1.5 w-7 h-7 rounded-full bg-gaming-accent/10 flex items-center justify-center group-hover/qi:bg-gaming-accent transition-all duration-200">
                      <Play className="w-3 h-3 text-gaming-accent group-hover/qi:text-black fill-current" />
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* ═══════════════════ BIBLIOTHÈQUE ═══════════════════ */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Library className="w-4 h-4 text-gaming-accent" />
              <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">
                Bibliothèque
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* ── Mes jeux ── */}
              <div
                onClick={() => setGamingView('library')}
                className="group/lib relative h-32 rounded-xl overflow-hidden cursor-pointer border border-blue-800/30 hover:border-blue-500/40 transition-all duration-300 hover:shadow-lg hover:shadow-blue-900/20"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-blue-800/70 to-indigo-900/90 group-hover/lib:from-blue-800 transition-colors duration-500" />
                {/* Decorative gaming icons */}
                <div className="absolute top-4 right-5 w-8 h-8 bg-blue-600/20 rounded-full border border-blue-500/15 flex items-center justify-center group-hover/lib:-translate-y-1.5 group-hover/lib:scale-110 transition-all duration-500">
                  <Gamepad2 className="w-3.5 h-3.5 text-blue-400/40" />
                </div>
                <div className="absolute bottom-4 right-16 w-6 h-6 bg-indigo-700/20 rounded-full border border-indigo-600/15 flex items-center justify-center group-hover/lib:translate-y-1 group-hover/lib:scale-110 transition-all duration-500 delay-75">
                  <Zap className="w-3 h-3 text-indigo-400/35" />
                </div>
                <div className="absolute top-2 right-16 w-5 h-5 bg-sky-600/15 rounded-full border border-sky-500/10 flex items-center justify-center group-hover/lib:-translate-x-1 group-hover/lib:scale-110 transition-all duration-500 delay-150">
                  <Star className="w-2.5 h-2.5 text-sky-400/30" />
                </div>

                <div className="relative z-10 h-full flex flex-col justify-center px-5">
                  {libraryGames.length > 0 && (
                    <span className="absolute top-2.5 left-5 text-[10px] font-medium text-blue-300/60 bg-blue-900/40 px-1.5 py-0.5 rounded-full border border-blue-700/20">
                      {libraryGames.length} jeu{libraryGames.length > 1 ? 'x' : ''}
                    </span>
                  )}
                  <h3 className="text-xl font-black text-white mb-0.5">Mes jeux</h3>
                  <p className="text-[11px] text-blue-200/50">
                    Bibliothèque · Installés · Favoris
                  </p>
                  <div className="flex items-center gap-1 mt-2 text-blue-300/60 group-hover/lib:text-blue-300 group-hover/lib:translate-x-0.5 transition-all duration-300">
                    <span className="text-[11px] font-semibold">Ouvrir</span>
                    <ChevronRight className="w-3 h-3" />
                  </div>
                </div>
              </div>

              {/* ── Minecraft ── */}
              <div
                onClick={() => setGamingView('minecraft')}
                className="group/mc relative h-32 rounded-xl overflow-hidden cursor-pointer border border-emerald-800/30 hover:border-emerald-500/40 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-900/20"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/90 via-emerald-800/70 to-green-900/90 group-hover/mc:from-emerald-800 transition-colors duration-500" />
                <div className="absolute top-3 right-4 w-7 h-7 bg-emerald-600/25 rounded-sm border border-emerald-500/15 group-hover/mc:-translate-y-1 group-hover/mc:rotate-6 transition-all duration-500" />
                <div className="absolute top-8 right-14 w-5 h-5 bg-amber-700/25 rounded-sm border border-amber-600/15 group-hover/mc:-translate-y-1.5 group-hover/mc:-rotate-6 transition-all duration-500 delay-75" />
                <div className="absolute bottom-3 right-6 w-9 h-9 bg-stone-600/25 rounded-sm border border-stone-500/15 group-hover/mc:-translate-y-1 group-hover/mc:rotate-3 transition-all duration-500 delay-100" />

                <div className="relative z-10 h-full flex flex-col justify-center px-5">
                  {profiles.length > 0 && (
                    <span className="absolute top-2.5 left-5 text-[10px] font-medium text-emerald-300/60 bg-emerald-900/40 px-1.5 py-0.5 rounded-full border border-emerald-700/20">
                      {profiles.length} profil{profiles.length > 1 ? 's' : ''}
                    </span>
                  )}
                  <h3 className="text-xl font-black text-white mb-0.5">Minecraft</h3>
                  <p className="text-[11px] text-emerald-200/50">
                    Profils · Mods · Serveurs · Shaders
                  </p>
                  <div className="flex items-center gap-1 mt-2 text-emerald-300/60 group-hover/mc:text-emerald-300 group-hover/mc:translate-x-0.5 transition-all duration-300">
                    <span className="text-[11px] font-semibold">Explorer</span>
                    <ChevronRight className="w-3 h-3" />
                  </div>
                </div>
              </div>

              {/* ── Erozion Go ── */}
              <div
                onClick={() => setGamingView('hosting')}
                className="group/host relative h-32 rounded-xl overflow-hidden cursor-pointer border border-cyan-800/30 hover:border-cyan-500/40 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-900/20"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/90 via-sky-800/70 to-blue-900/90 group-hover/host:from-cyan-800 transition-colors duration-500" />
                <div className="absolute top-4 right-4 w-8 h-8 bg-cyan-600/20 rounded-lg border border-cyan-500/15 flex items-center justify-center group-hover/host:-translate-y-1 group-hover/host:rotate-6 transition-all duration-500">
                  <Server className="w-4 h-4 text-cyan-400/40" />
                </div>
                <div className="absolute top-2 right-16 w-5 h-5 bg-sky-700/15 rounded-lg rotate-12 border border-sky-600/10 group-hover/host:rotate-45 group-hover/host:scale-110 transition-all duration-500 delay-100" />
                <div className="absolute bottom-3 right-8 w-6 h-6 bg-blue-600/15 rounded-lg -rotate-12 border border-blue-500/10 group-hover/host:rotate-6 group-hover/host:-translate-y-1.5 transition-all duration-500 delay-75" />

                <div className="relative z-10 h-full flex flex-col justify-center px-5">
                  <h3 className="text-xl font-black text-white mb-0.5">Erozion Go</h3>
                  <p className="text-[11px] text-cyan-200/50">
                    Serveurs MC · Console · Backups
                  </p>
                  <div className="flex items-center gap-1 mt-2 text-cyan-300/60 group-hover/host:text-cyan-300 group-hover/host:translate-x-0.5 transition-all duration-300">
                    <span className="text-[11px] font-semibold">Gérer</span>
                    <ChevronRight className="w-3 h-3" />
                  </div>
                </div>
              </div>

              {/* ── En revue (admin only) ── */}
              {isAdmin && (
                <div
                  onClick={() => setGamingView('review-library')}
                  className="group/rev relative h-32 rounded-xl overflow-hidden cursor-pointer border border-amber-800/30 hover:border-amber-500/40 transition-all duration-300 hover:shadow-lg hover:shadow-amber-900/20"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-900/90 via-amber-800/70 to-orange-900/90 group-hover/rev:from-amber-800 transition-colors duration-500" />
                  <div className="absolute top-4 right-5 w-8 h-8 bg-amber-600/20 rounded-full border border-amber-500/15 flex items-center justify-center group-hover/rev:-translate-y-1.5 group-hover/rev:scale-110 transition-all duration-500">
                    <Shield className="w-3.5 h-3.5 text-amber-400/40" />
                  </div>

                  <div className="relative z-10 h-full flex flex-col justify-center px-5">
                    <h3 className="text-xl font-black text-white mb-0.5">En revue</h3>
                    <p className="text-[11px] text-amber-200/50">
                      Tester · Approuver · Rejeter
                    </p>
                    <div className="flex items-center gap-1 mt-2 text-amber-300/60 group-hover/rev:text-amber-300 group-hover/rev:translate-x-0.5 transition-all duration-300">
                      <span className="text-[11px] font-semibold">Ouvrir</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ═══════════════════ FEATURED ═══════════════════ */}
          {featuredGames.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-gaming-accent" />
                  <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">En vedette</h2>
                </div>
                <button
                  onClick={() => setGamingView('store')}
                  className="text-[11px] text-gaming-accent hover:text-gaming-accent-hover flex items-center gap-0.5 transition-colors"
                >
                  Voir le store <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {featuredGames.map(game => (
                  <GameCard key={game.id} game={game} owned={libraryGames.some(g => g.id === game.id)} />
                ))}
              </div>
            </section>
          )}

          {/* ═══════════════════ LIBRARY ═══════════════════ */}
          {libraryGames.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Library className="w-4 h-4 text-gaming-accent" />
                <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">Ma Bibliothèque</h2>
                <span className="text-[10px] text-text-muted bg-bg-tertiary px-1.5 py-0.5 rounded-full">
                  {libraryGames.length}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {libraryGames.slice(0, 6).map(game => (
                  <GameCard key={game.id} game={game} owned />
                ))}
              </div>
            </section>
          )}

          {/* ═══════════════════ POPULAR ═══════════════════ */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-gaming-accent" />
                <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">Populaire</h2>
              </div>
              <button
                onClick={() => setGamingView('store')}
                className="text-[11px] text-gaming-accent hover:text-gaming-accent-hover flex items-center gap-0.5 transition-colors"
              >
                Store complet <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            {isLoadingStore ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-[2/3] rounded-lg bg-bg-tertiary animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {(popularGames.length > 0 ? popularGames : storeGames).map(game => (
                  <GameCard key={game.id} game={game} owned={libraryGames.some(g => g.id === game.id)} />
                ))}
              </div>
            )}
          </section>

        </div>
      </div>
    </div>
  );
};

export default GamingHubHome;
